import { useLazyQuery, useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import { Alert, Box, Button, Chip, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Permission, WasteInvoiceDetailQuery } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useParams } from "react-router-dom";

import { ManifestDetails as ManifestDetailsV2 } from "../Manifests/ManifestDetails";
import { ManifestDetails as ManifestDetailsV1 } from "../Manifests/ManifestDetailsV1";
import { InvoiceLinesTable } from "./Detail/InvoiceLinesTable";
import { GET_WASTE_INVOICE_DOCUMENT_DOWNLOAD_URL, WASTE_INVOICE_DETAIL_QUERY } from "./queries";
import { formatInvoiceDate, formatInvoiceMoney, formatServicePeriod } from "./utils";

type InvoiceDetail = NonNullable<WasteInvoiceDetailQuery["wasteInvoice"]>;

function DetailFact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Box>
  );
}

function InvoiceHeader({
  invoiceNumber,
  canDownloadInvoiceDocument,
  downloadUrlLoading,
  onDownload,
}: {
  invoiceNumber: string;
  canDownloadInvoiceDocument: boolean;
  downloadUrlLoading: boolean;
  onDownload: () => void;
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent="space-between"
      spacing={2}
    >
      <Typography variant="h5" component="h1">
        Invoice {invoiceNumber}
      </Typography>
      {canDownloadInvoiceDocument && (
        <Button
          size="small"
          startIcon={<Download />}
          loading={downloadUrlLoading}
          onClick={onDownload}
        >
          Download invoice document
        </Button>
      )}
    </Stack>
  );
}

function InvoiceContent({
  invoice,
  tenantId,
  onManifestClick,
}: {
  invoice: InvoiceDetail;
  tenantId: string;
  onManifestClick: (manifestId: string) => void;
}) {
  return (
    <Stack spacing={2.5}>
      <Box>
        <Chip size="small" variant="outlined" label={invoice.vendor.name} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        <DetailFact label="Invoice date" value={formatInvoiceDate(invoice.invoiceDate)} />
        <DetailFact
          label="Service period"
          value={formatServicePeriod(invoice.servicePeriodStart, invoice.servicePeriodEnd)}
        />
        <DetailFact label="Invoice total" value={formatInvoiceMoney(invoice.invoiceTotal)} />
        <DetailFact label="Line count" value={invoice.lineCount.toLocaleString()} />
      </Box>
      <Divider />
      <InvoiceLinesTable
        key={invoice.id}
        invoiceId={invoice.id}
        tenantId={tenantId}
        onManifestClick={onManifestClick}
      />
    </Stack>
  );
}

export function WasteInvoiceDetail() {
  const { wasteInvoiceId } = useParams<{ wasteInvoiceId: string }>();
  const { tenantId } = useTenant();
  const showManifestViewsV2 = useManifestViewsV2();
  const {
    selectedId: processedManifestId,
    pushIdToUrl: openManifest,
    popIdFromUrl: closeManifest,
  } = useSelectedIdInUrl("processedManifestId");
  const { hasPermissions } = useAuthorization();
  const alerts = useAlerts();
  const canDownloadInvoiceDocument = hasPermissions([
    Permission.ReadAllFacility,
    Permission.WriteAllFacility,
  ]);
  const { data, loading, error } = useQuery(WASTE_INVOICE_DETAIL_QUERY, {
    variables: { id: wasteInvoiceId ?? "" },
    skip: !wasteInvoiceId,
    fetchPolicy: "cache-and-network",
  });
  const [getDownloadUrl, { loading: downloadUrlLoading }] = useLazyQuery(
    GET_WASTE_INVOICE_DOCUMENT_DOWNLOAD_URL,
    { fetchPolicy: "network-only" }
  );
  const invoice = data?.wasteInvoice;

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    {
      label: "Invoice Tracking",
      to: tenantId ? `/o/${tenantId}/waste/invoices` : undefined,
    },
    ...(invoice ? [{ label: invoice.invoiceNumber }] : []),
  ]);

  const downloadInvoiceDocument = async () => {
    if (!wasteInvoiceId || !invoice?.sourceDocument) return;
    try {
      const result = await getDownloadUrl({ variables: { id: wasteInvoiceId } });
      const downloadUrl = result.data?.getWasteInvoiceDocumentDownloadUrl;
      if (!downloadUrl) {
        alerts.error("Unable to download invoice document");
        return;
      }
      window.open(downloadUrl, "_blank", "noopener");
    } catch (downloadError) {
      alerts.error(
        "Unable to download invoice document",
        downloadError instanceof Error ? downloadError : undefined
      );
    }
  };

  if (loading && !invoice) {
    return (
      <Stack spacing={2} sx={{ px: 3, py: 4 }}>
        <Skeleton width="45%" />
        <Skeleton height={90} />
        <Skeleton height={420} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 3, py: 4 }}>
        <Alert severity="error">{error.message}</Alert>
      </Box>
    );
  }

  if (!invoice || !tenantId) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack spacing={3} sx={{ px: 3, py: 4 }}>
        <InvoiceHeader
          invoiceNumber={invoice.invoiceNumber}
          canDownloadInvoiceDocument={canDownloadInvoiceDocument && invoice.sourceDocument != null}
          downloadUrlLoading={downloadUrlLoading}
          onDownload={downloadInvoiceDocument}
        />
        <InvoiceContent invoice={invoice} tenantId={tenantId} onManifestClick={openManifest} />
      </Stack>
      {showManifestViewsV2 ? (
        <ManifestDetailsV2
          processedManifestId={processedManifestId}
          open={!!processedManifestId}
          onClose={closeManifest}
        />
      ) : (
        <ManifestDetailsV1
          processedManifestId={processedManifestId}
          open={!!processedManifestId}
          onClose={closeManifest}
        />
      )}
    </>
  );
}
